import axios from 'axios'

import apiClient from '../api-client'

import type {
  AuditShare,
  AuditVersion,
  ListingAuditListItem,
  ListingRemakeAdoptResult,
  ListingRemakeFull,
  ListingRemakeSlot,
} from './listing-builder.types'

export type {
  ListingAuditListItem,
  ListingRemakeAdoptResult,
  ListingRemakeFull,
  ListingRemakeSlot,
} from './listing-builder.types'
export type { AuditShare, AuditVersion } from './listing-builder.types'
export type { ListingAuditReport } from './listing-builder.types'

const V2 = '/api/v2/listing-builder'
const V1 = '/api/v1'

export interface MarketplaceProfile {
  id: string | null
  marketplace_code: string
  display_name: string
  is_system: boolean
}

export interface ListingAuditStartResult {
  job_id: string
  audit_product_id?: string | null
  status: string
}

export interface ListingAuditStatus {
  id: string
  source_ref: string
  market_tld: string
  status: string
  current_step: string | null
  progress: {
    current: number
    total: number
    percentage: number
    frontend_step: number
    frontend_total: number
  }
  source_type: string
}

export interface ListingRemakeStatus {
  remake_id: string | null
  marketplace_code?: string
  status: string
  version_number?: number
  error_message?: string | null
}

export async function listMarketplaceProfiles(): Promise<MarketplaceProfile[]> {
  const resp = await apiClient.get(`${V1}/marketplace-profiles`)
  return resp.data?.profiles ?? []
}

export async function startListingAudit(data: {
  source_ref: string
  source_type?: 'asin' | 'manual' | 'channel'
  market_tld?: string
  report_language?: string
  mode?: 'small' | 'standard' | 'custom'
  channel_id?: string
  title?: string
  bullets?: string[]
  description?: string
  brand?: string
  image_asset_ids?: string[]
  gtin?: string
  sku?: string
  price?: string
  category?: string
}): Promise<ListingAuditStartResult> {
  const resp = await apiClient.post(`${V2}/audits`, data)
  return resp.data
}

export async function getListingAuditStatus(jobId: string): Promise<ListingAuditStatus> {
  const resp = await apiClient.get(`${V2}/audits/${jobId}`)
  return resp.data
}

export async function getListingAuditReport(jobId: string): Promise<Record<string, unknown>> {
  const resp = await apiClient.get(`${V2}/audits/${jobId}/report`)
  return resp.data
}

export async function reauditListingAudit(
  jobId: string,
): Promise<{ job_id: string; status: string }> {
  const resp = await apiClient.post(`${V2}/audits/${jobId}/reaudit`, {})
  return resp.data
}

export async function startListingRemake(
  jobId: string,
  data: { marketplace_code: string; sales_channel_id?: string; fields?: string[] },
): Promise<{ remake_id: string; status: string; version_number: number }> {
  const resp = await apiClient.post(`${V2}/audits/${jobId}/remake`, data)
  return resp.data
}

export async function getListingRemakeStatus(jobId: string): Promise<ListingRemakeStatus> {
  const resp = await apiClient.get(`${V2}/audits/${jobId}/remake/status`)
  return resp.data
}

export async function listListingAudits(params: {
  limit?: number
  offset?: number
  search?: string
}): Promise<{ items: ListingAuditListItem[]; total: number }> {
  const resp = await apiClient.get(`${V2}/audits`, { params })
  return resp.data
}

export async function deleteListingAudit(jobId: string): Promise<void> {
  await apiClient.delete(`${V2}/audits/${jobId}`)
}

export async function getListingRemakeFull(remakeId: string): Promise<ListingRemakeFull> {
  const resp = await apiClient.get(`${V2}/remakes/${remakeId}`)
  return resp.data
}

export async function getListingRemakeVersions(jobId: string): Promise<{
  versions: Array<{ id: string; marketplace_code: string; version_number: number; status: string }>
}> {
  const resp = await apiClient.get(`${V2}/audits/${jobId}/remake/versions`)
  return resp.data
}

export async function getListingRemakeSlots(remakeId: string): Promise<{
  slots: ListingRemakeSlot[]
}> {
  const resp = await apiClient.get(`${V2}/remakes/${remakeId}/slots`)
  return resp.data
}

export async function saveListingRemakeEdits(
  remakeId: string,
  edits: Record<string, unknown>,
): Promise<void> {
  await apiClient.put(`${V2}/remakes/${remakeId}`, { edits })
}

export async function cancelListingRemake(remakeId: string): Promise<void> {
  await apiClient.post(`${V2}/remakes/${remakeId}/cancel`, {})
}

export async function adoptListingRemakeResult(remakeId: string): Promise<ListingRemakeAdoptResult> {
  const resp = await apiClient.post(`${V2}/remakes/${remakeId}/adopt`, {})
  return resp.data
}

export async function listAuditVersions(jobId: string): Promise<{ versions: AuditVersion[] }> {
  const resp = await apiClient.get(`${V2}/audits/${jobId}/versions`)
  return resp.data
}

export async function startBatchAudit(data: {
  asins: string[]
  market_tld: string
  report_language?: string
  mode?: string
}): Promise<{ jobs: Array<{ job_id: string; source_ref: string; status: string }> }> {
  const resp = await apiClient.post(`${V2}/audits/batch`, data)
  return resp.data
}

export async function createAuditShare(
  jobId: string,
  data?: { password?: string },
): Promise<AuditShare> {
  const resp = await apiClient.post(`${V2}/audits/${jobId}/share`, data ?? {})
  return resp.data
}

export async function getAuditShare(jobId: string): Promise<AuditShare | null> {
  try {
    const resp = await apiClient.get(`${V2}/audits/${jobId}/share`)
    return resp.data
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null
    }
    throw err
  }
}

export async function deactivateAuditShare(jobId: string): Promise<void> {
  await apiClient.delete(`${V2}/audits/${jobId}/share`)
}

export async function getPublicAudit(
  token: string,
  password?: string,
): Promise<Record<string, unknown>> {
  const resp = await apiClient.post(`/api/v2/public/listing-builder/audit/${token}`, { password })
  return resp.data
}
